import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { SubscriptionForm } from "features/subscription/SubscriptionForm";
import { useSubscriptionFormOptions } from "features/subscription/hooks/useSubscriptionFormOptions";
import { useUserData } from "features/userData/context";
import { _ } from "i18n";
import type { SubscriptionUpsertRequest } from "libs/apiClient";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import DeleteConfirmationModal from "pages/productManagement/components/DeleteConfirmationModal";
import { Permission, Resource } from "pages/productManagement/types";
import NotFoundPage from "pages/errorPages/NotFoundPage";
import { useToast } from "ui-kit";
import "style/discoverPage.css";
import "features/subscription/SubscriptionEditPage.css";

export function SubscriptionEditPage() {
	const { id } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { addErrorToast, addSuccessToast } = useToast();
	const userData = useUserData();

	const isNew = !id || id === "new";
	const subscriptionId = !isNew ? Number.parseInt(id, 10) : undefined;
	const isValidId = subscriptionId !== undefined && !Number.isNaN(subscriptionId);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const {
		productsConfig,
		countriesWithContinents,
		subscriptionProducts,
		ownershipOptions,
		teamGroupOptions,
		competitionOptions,
		competitionEditionOptions,
		eyeballAddons,
		discoverCountryOptions,
		isLoading: isOptionsLoading,
	} = useSubscriptionFormOptions();

	const { data: subscriptionResponse, isLoading: isSubscriptionLoading, error: subscriptionError } = client.getSubscription.useQuery(
		{ id: subscriptionId! },
		{
			enabled: isValidId,
			retry: false,
		},
	);
	const subscription = subscriptionResponse?.data;

	const createMutation = client.postSubscription.useMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["getSubscriptions"] });
		},
	});
	const updateMutation = client.putSubscription.useMutation({
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["getSubscriptions"] });
			queryClient.invalidateQueries({ queryKey: ["getSubscription", { id: variables.id }] });
		},
	});
	const deleteMutation = client.deleteSubscriptionV2.useMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["getSubscriptions"] });
		},
	});

	const canEditPipedriveDealId = useMemo(() => {
		return (
			userData.isSuperAdmin ||
			!!userData.permissions?.find(
				(permission) => permission.resource === Resource.SUBSCRIPTION_DEAL_ID && permission.permission === Permission.EDIT,
			)
		);
	}, [userData]);

	useEffect(() => {
		setScrollBehavior();
	}, []);

	const handleSubmit = useCallback(
		async (payload: SubscriptionUpsertRequest) => {
			setSubmitError(null);
			try {
				if (isNew) {
					const response = await createMutation.mutateAsync({ body: payload });
					if (response?.data) {
						addSuccessToast(response.data);
					}
					navigate("/subscription");
					return;
				}

				if (!isValidId) return;
				const response = await updateMutation.mutateAsync({ id: subscriptionId, body: payload });
				if (response?.data) {
					addSuccessToast(response.data);
				}
				navigate("/subscription");
			} catch (error: unknown) {
				const message = extractMutationErrorMessage(error);
				setSubmitError(message);
			}
		},
		[
			addErrorToast,
			addSuccessToast,
			createMutation,
			isNew,
			isValidId,
			navigate,
			subscriptionId,
			updateMutation,
		],
	);

	const handleDelete = useCallback(async () => {
		if (!isValidId) return;
		try {
			const response = await deleteMutation.mutateAsync({ id: subscriptionId });
			if (response?.data) {
				addSuccessToast(response.data);
			}
			navigate("/subscription");
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		} finally {
			setShowDeleteModal(false);
		}
	}, [addErrorToast, addSuccessToast, deleteMutation, isValidId, navigate, subscriptionId]);

	if (!isNew && !isValidId) {
		return <NotFoundPage />;
	}

	if (!isNew && subscriptionError) {
		const status = (subscriptionError as { response?: { status?: number } })?.response?.status;
		if (status === 404) {
			return <NotFoundPage />;
		}
	}

	const isLoading = isOptionsLoading || (!isNew && isSubscriptionLoading);
	const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

	const pageTitle = isNew
		? _("label.add.subscription")
		: `${_("label.subscription")} #${subscription?.id ?? subscriptionId}`;

	const deleteTitle = subscription
		? `${subscription.product ?? ""}${subscription.proClubName ? ` - ${subscription.proClubName}` : ""}`
		: String(subscriptionId ?? "");

	return (
		<div className="subscriptionEditPage-container">
			<div className="pageHeading">
				<div>
					<Link to="/subscription" className="subscriptionEditPage-backLink">
						← {_("label.subscriptions_management")}
					</Link>
					<h1>{pageTitle}</h1>
				</div>
				{!isNew && (
					<button type="button" className="btn btn--red" onClick={() => setShowDeleteModal(true)}>
						{_("btn.delete")}
					</button>
				)}
			</div>

			{isLoading ? (
				<Loader />
			) : (
				<div className="discover-card">
					<SubscriptionForm
						row={isNew ? undefined : subscription}
						productsConfig={productsConfig}
						countriesWithContinents={countriesWithContinents}
						subscriptionProducts={subscriptionProducts}
						ownershipOptions={ownershipOptions}
						teamGroupOptions={teamGroupOptions}
						competitionOptions={competitionOptions}
						competitionEditionOptions={competitionEditionOptions}
						eyeballAddons={eyeballAddons}
						discoverCountryOptions={discoverCountryOptions}
						canEditPipedriveDealId={canEditPipedriveDealId}
						submitError={submitError}
						isSubmitting={isSubmitting}
						onSubmit={handleSubmit}
					/>
				</div>
			)}

			<DeleteConfirmationModal
				show={showDeleteModal}
				title={deleteTitle}
				onClose={() => setShowDeleteModal(false)}
				onDelete={handleDelete}
			/>
		</div>
	);
}

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") return err.json;
		if (err.json?.message) return err.json.message;
		if (err.message) return err.message;
	}
	return _("message.error.generic");
}

export default SubscriptionEditPage;
